/**
 * Authority tier mapping for source authoritativeness scores.
 *
 * Maps the backend-provided authoritativeness_score (0-100) to
 * user-facing tier labels and badge variants for display in
 * SourceCard hover popover and potentially ReferenceCard.
 */
import type { TranslationKey } from '@heidi/runtime/i18n';

export interface AuthorityTier {
  /** Translation key for the user-facing label, e.g. "chat.reliable" */
  labelKey: TranslationKey;
  /** Minimum score (inclusive) for this tier */
  minScore: number;
  /** Tailwind background class for the badge */
  bgClass: string;
  /** Tailwind text colour class for the badge */
  textClass: string;
}

/**
 * V3 cite-score tier configuration.
 * Extends AuthorityTier with additional styling for the square score badge.
 */
export interface CiteScoreTier extends AuthorityTier {
  /** Tailwind background class for the square score box */
  scoreBgClass: string;
  /** Tailwind text colour class for the score number */
  scoreTextClass: string;
}

/**
 * Chat authority badges are intentionally simplified to two states:
 *
 * - Reliable: score >= 70 (includes what used to be "Highly reliable")
 * - Unverified: score < 40
 * - No badge: 40–69 (what used to be "Somewhat reliable")
 *
 * Colour pairs follow the Figma spec (node 6334:1404881):
 *   Reliable   -> skies-200  / skies-800
 *   Unverified -> bark-100   / bark-600
 */
const RELIABLE_TIER: AuthorityTier = {
  labelKey: 'chat.reliable',
  minScore: 70,
  bgClass: 'bg-skies-200',
  textClass: 'text-skies-800',
};

/** Scores below this threshold are considered unverified. */
const UNVERIFIED_TIER_MAX_SCORE = 40;

const UNVERIFIED_TIER: AuthorityTier = {
  labelKey: 'chat.unverified',
  minScore: 0,
  bgClass: 'bg-bark-100',
  textClass: 'text-bark-600',
};

/**
 * Get the authority tier for a given score.
 * Returns null if score is undefined/null (badge should be hidden).
 */
export function getAuthorityTier(score: number | undefined | null): AuthorityTier | null {
  if (score === undefined || score === null) return null;

  if (score >= RELIABLE_TIER.minScore) return RELIABLE_TIER;
  if (score < UNVERIFIED_TIER_MAX_SCORE) return UNVERIFIED_TIER;
  return null;
}

// ============================================================================
// V3 Impact Score tiers (Figma node 7520:32170)
//
// Labels use neutral numeric tiers (Option E) — see
// docs/design-docs/source-card/impact-score-labels.md for rationale and
// alternatives that were considered.
//
// Tiers map the backend authoritativeness_score to user-facing labels:
//   ≥70  → Tier 1 (green box)
//   40–69 → Tier 2 (blue box)
//   <40  → Tier 3 (warm grey box)
// ============================================================================

const TIER_1_CITE_SCORE: CiteScoreTier = {
  labelKey: 'chat.impactTier.tier1',
  minScore: 70,
  bgClass: 'bg-skies-200',
  textClass: 'text-skies-800',
  scoreBgClass: 'bg-forest-200',
  scoreTextClass: 'text-forest-900',
};

const TIER_2_CITE_SCORE: CiteScoreTier = {
  labelKey: 'chat.impactTier.tier2',
  minScore: 40,
  bgClass: 'bg-skies-200',
  textClass: 'text-skies-800',
  scoreBgClass: 'bg-skies-200',
  scoreTextClass: 'text-skies-900',
};

const TIER_3_CITE_SCORE: CiteScoreTier = {
  labelKey: 'chat.impactTier.tier3',
  minScore: 0,
  bgClass: 'bg-bark-100',
  textClass: 'text-bark-600',
  scoreBgClass: 'bg-bark-100',
  scoreTextClass: 'text-bark-600',
};

/**
 * V3: Get the impact score tier for a given authoritativeness score.
 * Unlike v2, this returns a tier for all valid scores (no hidden range).
 */
export function getCiteScoreTier(score: number | undefined | null): CiteScoreTier | null {
  if (score === undefined || score === null) return null;

  if (score >= TIER_1_CITE_SCORE.minScore) return TIER_1_CITE_SCORE;
  if (score >= TIER_2_CITE_SCORE.minScore) return TIER_2_CITE_SCORE;
  return TIER_3_CITE_SCORE;
}

/**
 * V3: Get the user-facing tier label for an impact score.
 * Tier 1 (≥70), Tier 2 (40–69), Tier 3 (<40).
 */
export function getCiteScoreLabel(
  score: number | undefined | null
): TranslationKey | null {
  const tier = getCiteScoreTier(score);
  if (!tier) return null;
  return tier.labelKey;
}

// ============================================================================
// V3 Confidence tiers (Figma node 7556:8122)
//
// Qualitative labels for non-journal sources that have an
// authoritativeness_score but no journal impact factor. Displayed as a
// simple coloured pill badge — visually distinct from the cite score badge
// which has a numeric box + tier label + footnote.
//
// Uses the same score thresholds and colour ramp as cite score tiers:
//   ≥70  → High confidence   (green pill)
//   40–69 → Moderate confidence (blue pill)
//   <40  → Low confidence    (warm grey pill)
// ============================================================================

export interface ConfidenceTier {
  labelKey: TranslationKey;
  minScore: number;
  /** Tailwind background class for the pill badge */
  bgClass: string;
  /** Tailwind text colour class for the pill badge */
  textClass: string;
}

const HIGH_CONFIDENCE_TIER: ConfidenceTier = {
  labelKey: 'chat.confidence.high',
  minScore: 70,
  bgClass: 'bg-forest-200',
  textClass: 'text-forest-900',
};

const MODERATE_CONFIDENCE_TIER: ConfidenceTier = {
  labelKey: 'chat.confidence.moderate',
  minScore: 40,
  bgClass: 'bg-skies-200',
  textClass: 'text-skies-900',
};

const LOW_CONFIDENCE_TIER: ConfidenceTier = {
  labelKey: 'chat.confidence.low',
  minScore: 0,
  bgClass: 'bg-bark-100',
  textClass: 'text-bark-600',
};

/**
 * V3: Get the confidence tier for a given authoritativeness score.
 * Used for non-journal sources that lack an impact factor.
 * High (≥70), Moderate (40–69), Low (<40).
 */
export function getConfidenceTier(
  score: number | undefined | null
): ConfidenceTier | null {
  if (score === undefined || score === null) return null;

  if (score >= HIGH_CONFIDENCE_TIER.minScore) return HIGH_CONFIDENCE_TIER;
  if (score >= MODERATE_CONFIDENCE_TIER.minScore) return MODERATE_CONFIDENCE_TIER;
  return LOW_CONFIDENCE_TIER;
}
